<template>
  <v-dialog
    v-model="internalValue"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    content-class="media-viewer-dialog"
  >
    <div class="mv-container" :class="{ 'mv-fullscreen': isFullscreen }">
      <!-- Toolbar -->
      <div class="mv-toolbar">
        <div class="mv-toolbar-left">
          <span class="mv-title"
            >Preview - {{ activeItem ? activeItem.name : "Media" }} ({{
              currentIndex + 1
            }}/{{ items.length }})</span
          >
        </div>
        <div class="mv-toolbar-right">
          <button class="mv-btn" @click="downloadMedia" title="Download">
            <v-icon color="#42526E" size="20">mdi-tray-arrow-down</v-icon>
          </button>
          <button class="mv-btn" @click="toggleFullscreen" title="Fullscreen">
            <v-icon color="#42526E" size="20">mdi-fullscreen</v-icon>
          </button>
          <button class="mv-btn" @click="internalValue = false" title="Close">
            <v-icon color="#42526E" size="20">mdi-close</v-icon>
          </button>
        </div>
      </div>

      <!-- Main Content -->
      <div class="mv-content">
        <button
          v-if="items.length > 1"
          class="mv-nav-btn mv-prev"
          @click="prev"
          :disabled="currentIndex === 0"
        >
          <v-icon size="24" color="#42526E">mdi-arrow-left</v-icon>
        </button>

        <div class="mv-media-wrap" :class="{ 'mv-pdf-mode': activeItem && activeItem.type === 'document' }">
          <transition name="fade" mode="out-in">
            <div :key="currentIndex" class="mv-media-inner" :class="{ 'mv-pdf-mode': activeItem && activeItem.type === 'document' }">
              <img
                v-if="activeItem && activeItem.type === 'image'"
                :src="activeItem.url"
                class="mv-image"
                :style="imageStyle"
              />
              <video
                v-else-if="activeItem && activeItem.type === 'video'"
                :src="activeItem.url"
                controls
                autoplay
                class="mv-video"
                :style="imageStyle"
              ></video>
              <iframe
                v-else-if="activeItem && activeItem.type === 'document'"
                :src="activeItem.url"
                class="mv-pdf-iframe"
                frameborder="0"
              ></iframe>
            </div>
          </transition>
        </div>

        <button
          v-if="items.length > 1"
          class="mv-nav-btn mv-next"
          @click="next"
          :disabled="currentIndex === items.length - 1"
        >
          <v-icon size="24" color="#42526E">mdi-arrow-right</v-icon>
        </button>
      </div>

      <!-- Bottom controls -->
      <div
        class="mv-footer-controls"
        v-if="activeItem && activeItem.type !== 'document'"
      >
        <div class="mv-zoom-toolbar">
          <button class="mv-zoom-btn" @click="zoomOut" title="Zoom Out">
            <v-icon size="20" color="#42526E">mdi-magnify-minus</v-icon>
          </button>
          <div class="mv-zoom-divider"></div>
          <button class="mv-zoom-btn" @click="resetZoom" title="Reset Zoom">
            <v-icon size="20" color="#42526E">mdi-backup-restore</v-icon>
          </button>
          <div class="mv-zoom-divider"></div>
          <button class="mv-zoom-btn" @click="zoomIn" title="Zoom In">
            <v-icon size="20" color="#42526E">mdi-magnify-plus</v-icon>
          </button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
export default {
  name: "MediaViewer",
  props: {
    value: Boolean,
    items: {
      type: Array,
      default: () => [], // Array of { url, type: 'image'|'video'|'document', name }
    },
    index: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isFullscreen: false,
      currentIndex: this.index,
      zoomLevel: 1,
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    activeItem() {
      return this.items[this.currentIndex] || null;
    },
    imageStyle() {
      return {
        transform: `scale(${this.zoomLevel})`,
      };
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.currentIndex = this.index;
        this.resetZoom();
      }
    },
    index(newVal) {
      this.currentIndex = newVal;
      this.resetZoom();
    },
  },
  methods: {
    prev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.resetZoom();
      }
    },
    next() {
      if (this.currentIndex < this.items.length - 1) {
        this.currentIndex++;
        this.resetZoom();
      }
    },
    zoomIn() {
      if (this.zoomLevel < 4) this.zoomLevel += 0.25;
    },
    zoomOut() {
      if (this.zoomLevel > 0.5) this.zoomLevel -= 0.25;
    },
    resetZoom() {
      this.zoomLevel = 1;
    },
    downloadMedia() {
      if (!this.activeItem) return;
      const link = document.createElement("a");
      link.href = this.activeItem.url;
      link.download = this.activeItem.name || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    openInNewTab(url) {
      window.open(url, "_blank");
    },
    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.log(`Error: ${err.message}`);
        });
      } else {
        document.exitFullscreen();
      }
    },
    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },
  },
  mounted() {
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
  },
  beforeDestroy() {
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
  },
};
</script>

<style scoped>
.mv-container.mv-fullscreen {
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  border-radius: 0 !important;
}
.mv-container {
  width: 95%;
  height: 90%;
  border-radius: 8px !important;
  background: #f4f5f7;
  display: flex;
  margin: auto;
  margin-top: 50px;
  flex-direction: column;
  position: relative;
  color: #172b4d;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
}

.mv-toolbar {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: #ffffff;
  border-bottom: 1px solid #dfe1e6;
  z-index: 10;
}
.mv-title {
  font-size: 16px;
  font-weight: 500;
  color: #172b4d;
}

.mv-toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mv-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
  background: transparent;
}
.mv-btn:hover {
  background: #ebecf0;
}

.mv-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
  padding: 0 32px;
  min-height: 0; /* ← critical for nested flex height calculation */
}

.mv-nav-btn {
  width: 48px;
  height: 48px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31);
  transition: all 0.2s;
  z-index: 5;
}
.mv-nav-btn:hover:not(:disabled) {
  background: #f4f5f7;
}
.mv-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: 0 0 1px rgba(9, 30, 66, 0.31);
}

.mv-media-wrap {
  flex: 1;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  overflow: hidden;
  min-height: 0; /* ← critical: allows flex child to shrink */
}

.mv-media-inner {
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.mv-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  box-shadow: 0 8px 16px -4px rgba(9, 30, 66, 0.25),
    0 0 1px rgba(9, 30, 66, 0.31);
  background: #ffffff;
  transition: transform 0.2s ease-out;
}
.mv-video {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  outline: none;
  box-shadow: 0 8px 16px -4px rgba(9, 30, 66, 0.25),
    0 0 1px rgba(9, 30, 66, 0.31);
  transition: transform 0.2s ease-out;
}
.mv-pdf-iframe {
  width: 100%;
  height: 100%;
  min-height: 500px;
  border: none;
  display: block;
  background: #fff;
}

/* PDF mode — remove padding so iframe fills edge-to-edge */
.mv-media-wrap.mv-pdf-mode {
  padding: 0;
  overflow: hidden;
}
.mv-media-inner.mv-pdf-mode {
  overflow: hidden;
  align-items: stretch;
  justify-content: stretch;
}

.mv-footer-controls {
  position: absolute;
  bottom: 32px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 10;
}

.mv-zoom-toolbar {
  display: flex;
  background: #ffffff;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31);
  overflow: hidden;
}

.mv-zoom-btn {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  transition: background 0.2s;
}
.mv-zoom-btn:hover {
  background: #ebecf0;
}
.mv-zoom-divider {
  width: 1px;
  background: #dfe1e6;
  margin: 12px 0;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-enter {
  opacity: 0;
  transform: scale(0.98);
}
.fade-leave-to {
  opacity: 0;
  transform: scale(1.02);
}
</style>
<style>
.media-viewer-dialog {
  margin: 0 !important;
}
</style>
